import moment from 'moment';

/**
 * @desc 날짜 문자열을 "YYYY.MM.DD" 형식으로 포맷하는 함수
 * @param {string} dateString - "YYYY-MM-DDTHH:mm:ss" 형식의 날짜 문자열
 * @returns "YYYY.MM.DD" 형식의 날짜 문자열
 */
export const formatDateDisplayInfo = (dateString) => {
  if (dateString === null || dateString === '') {
    return '-';
  } else {
    return moment(dateString).format('YYYY.MM.DD');
  }
};

/**
 * @desc 두 날짜 사이의 날짜를 배열로 반환하는 함수
 * @param {*} startDate
 * @param {*} endDate
 */
export const formatDatesBetween = (startDate, endDate) => {
  const dateArray = [];
  const currentDate = new Date(startDate);

  // startDate부터 endDate까지 반복
  while (currentDate <= endDate) {
    // 날짜를 "YYYY.MM.DD" 형식으로 포맷하여 배열에 추가
    dateArray.push(formatDateDisplayInfo(currentDate));
    // 다음 날로 이동
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dateArray;
};

/**
 * @desc 현재 날짜에서 이전 달의 연도와 월을 'YYYY-MM' 형식으로 반환하는 함수
 * @returns {string} 이전 달의 연도와 월을 'YYYY-MM' 형식으로 반환
 */
export const getPreviousMonth = (date) => {
  const currentDate = new Date(date); // 현재 날짜를 가져옴

  // 현재 날짜에서 이전 달의 첫 번째 날짜를 생성 (년은 그대로, 월만 -1)
  const previousMonthDate = new Date(
    currentDate.getFullYear(), // 현재 연도
    currentDate.getMonth() - 1, // 현재 월에서 1을 뺌 (0: 1월, 11: 12월)
    1 // 일자는 1일로 설정
  );

  // 이전 달의 연도와 월을 가져옴
  const year = previousMonthDate.getFullYear(); // 연도를 가져옴
  const month = String(previousMonthDate.getMonth() + 1).padStart(2, '0'); // 월을 가져오고, 2자리로 맞춤 (1월: 01, 12월: 12)

  return `${year}-${month}`; // 'YYYY-MM' 형식으로 반환
};

/**
 * @function getPreviousMonthPeriod
 * @desc 현재 날짜에서 이전 달의 시작일과 마지막 일을 'YYYY.MM.DD ~ YYYY.MM.DD' 형식으로 반환하는 함수
 * @returns {string} 이전 달의 시작일과 마지막 일을 'YYYY.MM.DD ~ YYYY.MM.DD' 형식으로 반환
 */
export const getPreviousMonthPeriod = () => {
  const currentDate = new Date(); // 현재 날짜를 가져옴

  // 현재 날짜에서 이전 달의 첫 번째 날짜 생성
  const firstDayOfPreviousMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    1
  );

  // 이전 달의 마지막 날짜 생성
  const lastDayOfPreviousMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    0
  );

  return formatPeriodDisplayInfo(
    `${firstDayOfPreviousMonth}~${lastDayOfPreviousMonth}`
  );
};
/**
 * 현재 날짜에서 한 달 뒤의 1일을 계산하는 함수입니다.
 *
 * @returns {Date} 한 달 뒤의 1일을 나타내는 Date 객체
 */
export const getFirstOfNextMonth = (dateString) => {
  const currentDate = new Date(dateString);

  // 현재 날짜에서 한 달 뒤로 설정
  const nextMonth = new Date(currentDate.setMonth(currentDate.getMonth() + 1));

  // 한 달 뒤의 1일로 설정
  const firstOfNextMonth = new Date(
    nextMonth.getFullYear(),
    nextMonth.getMonth(),
    1
  );

  return firstOfNextMonth;
};

/**
 * @desc priceEndAt 날짜가 현재 날짜보다 이전인지 확인하는 함수입니다.
 * @param {string} priceEndAt - 확인할 종료 날짜를 나타내는 문자열입니다. 일반적으로 ISO 8601 형식(예: "2023-04-25T16:22:35")을 따릅니다.
 * @returns {boolean} 종료 날짜가 현재 날짜보다 이전이면 `true`, 이후이면 `false`를 반환합니다.
 */
export const isPastEndDate = (priceEndAt) => {
  const currentDate = new Date(); // 현재 날짜 및 시간
  const endDate = new Date(priceEndAt); // priceEndAt을 Date 객체로 변환

  return endDate < currentDate; // priceEndAt이 현재 날짜보다 이전인지 확인
};

/**
 * @desc 기간을 포맷팅하는 함수
 * @param {string} periodDt - 결제 기간 (쉼표로 구분된 여러 기간 ex, 2025-01-01~2025-01-12,2025-01-12~2025-01-13)
 * @return {string} - 포맷된 기간 문자열
 */
export const formatPeriodDisplayInfo = (periodDt) => {
  if (!periodDt) return periodDt;

  const periods = periodDt.split(',');
  const formattedPeriods = [];

  for (let i = 0; i < periods.length; i++) {
    const range = periods[i].split('~');
    if (range.length === 2) {
      const startFormatted = formatDateDisplayInfo(range[0]);
      const endFormatted = formatDateDisplayInfo(range[1]).slice(5);
      formattedPeriods.push(
        i === 0
          ? `${startFormatted}~${endFormatted}`
          : `${startFormatted.slice(5)}~${endFormatted}`
      );
    }
  }

  return formattedPeriods.join(', ');
};

/**
 * @desc 연도와 월을 가져오는 함수
 * @return 'YYYY-MM'
 */
export const formatYearMonth = (dateString) => {
  return moment(dateString).format('YYYY-MM');
};

/**
 * @desc 날짜 문자열을 "YYYY.MM" 형식으로 포맷하는 함수
 * @param {string} dateString - "YYYY-MM-DDTHH:mm:ss" 형식의 날짜 문자열
 * @returns "YYYY.MM" 형식의 날짜 문자열
 */
export const formatYearMonthDisplayInfo = (dateString) => {
  return moment(dateString).format('YYYY.MM');
};

/**
 * @desc 날짜 문자열을 "MM" 형식으로 포맷하는 함수
 * @param {string} dateString - "YYYY-MM-DDTHH:mm:ss" 형식의 날짜 문자열
 * @returns "MM" 형식의 날짜 문자열
 */
export const formatMonthDisplayInfo = (dateString) => {
  return moment(dateString).format('MM');
};

/**
 * @function getNextMonth
 * @desc 입력된 날짜를 기준으로 다음 달을 계산하여 반환하는 함수
 * @param {string} dateString - ISO 8601 형식의 날짜 문자열 (예: "YYYY-MM-DDTHH:mm:ss.sssZ")
 * @returns {string} - 다음 달의 월 (MM 형식)
 */
export const getNextMonth = (dateString) => {
  const date = new Date(dateString); // 문자열을 Date 객체로 변환

  // 연산: 다음 달 계산 (12월이면 다음 연도로 넘어감)
  const nextMonth = (date.getMonth() + 1) % 12; // 다음 달 (0 ~ 11의 범위 유지)
  const month = String(nextMonth + 1).padStart(2, '0'); // 월을 1부터 시작하도록 보정

  return month;
};

/**
 * @function getMonth
 * @desc 입력된 날짜를 기준으로 달을 계산하여 반환하는 함수
 * @param {string} dateString - ISO 8601 형식의 날짜 문자열 (예: "YYYY-MM-DDTHH:mm:ss.sssZ")
 * @returns {string} - 다음 달의 월 (MM 형식)
 */
export const getMonth = (dateString) => {
  const date = new Date(dateString); // 문자열을 Date 객체로 변환

  const month = String(date.getMonth() + 1).padStart(2, '0'); // 월을 1부터 시작하도록 보정

  return month;
};

/**
 * @desc 날짜를 "YYYY-MM-DD" 형식으로 포맷하는 함수
 * @param {*} date
 * @returns "YYYY-MM-DD"  형식
 */
export const formatDateHyphen = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 월은 0부터 시작하므로 +1을 해줍니다.
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * @desc 현재 날짜와 시간을 "YYYY-MM-DDTHH:MM:SS" 형식으로 반환하는 함수입니다.
 * @returns {string} 현재 날짜와 시간
 */
export const getCurrentDateTime = () => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // 월은 0부터 시작하므로 +1
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * 입력된 날짜와 현재 시간의 차이를 계산하여
 * "몇 초 전", "몇 분 전", "몇 시간 전" 또는 날짜 형식(YYYY.MM.DD)으로 반환하는 함수
 * @param {string | Date} datetime - 비교할 대상 날짜
 * @returns {string} 변환된 시간 차이 문자열
 */
export const formatToDate = (datetime) => {
  // 오늘 날짜
  let now = moment(new Date());
  // 오늘과의 시간 차이
  let duration = moment.duration(now.diff(datetime));
  // 변환
  // asSeconds 를 하면 오늘과의 시간차이를
  // 초단위로 float datatype 으로 보여준다 (3.82 이런식)
  let seconds = duration.asSeconds();
  let minute = duration.asMinutes();
  let hours = duration.asHours();

  // 그래서 사용할 때는 parseInt 를 사용해 int 로 바꿔야 한다.
  if (minute < 1) {
    // 1분 미만이면 초 단위로 보여주고,
    return (
      moment(datetime).format('YYYY.MM.DD') + ' ' + Math.ceil(seconds) + '초 전'
    );
  } else if (hours < 1) {
    // 1시간 미만이면 분 단위로 보여주고
    return (
      moment(datetime).format('YYYY.MM.DD') + ' ' + Math.ceil(minute) + '분 전'
    );
  } else if (hours < 24) {
    // 하루 미만이면 시간으로 보여주고
    return (
      moment(datetime).format('YYYY.MM.DD') + ' ' + Math.ceil(hours) + '시간 전'
    );
  } else {
    // 하루 이상이면 날짜만 표시
    return moment(datetime).format('YYYY.MM.DD');
  }
};

/**
 * @function getNextMonthStartDate
 * @desc 입력된 날짜를 기준으로 다음 달 1일 00:00:00을 ISO 8601 형식으로 반환
 * @param {string} inputDate - 기준 날짜 (ISO 8601 형식: "YYYY-MM-DDTHH:mm:ssZ")
 * @returns {string} - 다음 달 1일 00:00:00 ISO 8601 형식
 */
export const getNextMonthStartDate = (inputDate) => {
  const date = new Date(inputDate); // 입력된 날짜를 Date 객체로 변환

  if (isNaN(date)) {
    throw new Error(
      "유효하지 않은 날짜 형식입니다. ISO 8601 형식을 사용하세요. 예: '2024-01-10T23:59:59+00:00'"
    );
  }

  // 다음 달 1일을 계산
  const nextMonth = date.getMonth() + 1; // 현재 월 + 1
  const year = date.getFullYear() + Math.floor(nextMonth / 12); // 월이 12를 넘어가면 연도 증가
  const month = nextMonth % 12; // 12월을 넘어가면 월 초기화

  // 다음 달 1일 00:00:00 생성
  const nextMonthStartDate = new Date(Date.UTC(year, month, 1, 0, 0, 0));

  return nextMonthStartDate.toISOString(); // ISO 8601 형식으로 반환
};

/**
 * @desc 30일동안 플래그를 추가하기 위한 유틸리티 함수 (moment 버전)
 * @param {string} date - 기준이 되는 날짜 (YYYY-MM-DD 형식 추천)
 * @returns {boolean} 해당 날짜가 기준으로부터 30일 이내인지 여부
 */
export const isWithin30DaysFrom = (date) => {
  // YYYY-MM-DD 또는 YYYY.MM.DD 형식을 모두 지원하도록 배열로 포맷 지정
  const originDate = moment(date, ['YYYY-MM-DD', 'YYYY.MM.DD']);
  const today = moment();

  const endDate = originDate.clone().add(30, 'days');

  return today.isSameOrAfter(originDate) && today.isSameOrBefore(endDate);
};

/**
 * @desc UPDATE인지 NEW인지 판별하기 위한 함수
 * @param {string} createAt - 등록일자 (YYYY-MM-DD 형식)
 * @param {string} updateAt - 수정일자 (YYYY-MM-DD 형식)
 * @returns {{  value: "NEW" | "UPDATE", date: string }}
 */
export const getNewUpdateDate = (createAt, updateAt) => {
  const created = moment(createAt, 'YYYY-MM-DD');
  const updated = moment(updateAt, 'YYYY-MM-DD');

  if (
    createAt &&
    updateAt &&
    updated.isValid() &&
    created.isValid() &&
    created.isBefore(updated)
  ) {
    return {
      value: 'UP',
      date: updated.format('YYYY.MM.DD'),
    };
  } else {
    return {
      value: 'N',
      date: created.format('YYYY.MM.DD'),
    };
  }
};

/**
 * @desc 메뉴 항목의 날짜 정보를 기반으로 태그 속성을 계산하는 함수
 * @param {Object} item - 날짜 정보를 포함한 객체 (createdAt/updatedAt 또는 createAt/updateAt)
 * @returns {{ isRecent: boolean, value: "NEW" | "UPDATE" | null }}
 */
export const getMenuTagProps = (item) => {
  if (!item) return { isRecent: false, value: null };

  const parsedCreatedAt = item.createdAt || item.createAt;
  const parsedUpdatedAt = item.updatedAt || item.updateAt;

  if (!parsedCreatedAt && !parsedUpdatedAt) {
    return { isRecent: false, value: null };
  }

  const { date, value } = getNewUpdateDate(
    parsedCreatedAt,
    parsedUpdatedAt
  );

  const isRecent = isWithin30DaysFrom(date);

  return { isRecent, value };
};

/**
 * 주어진 "YYYY.MM" 형식의 문자열을 기반으로,
 * 해당 월의 시작일과 마지막일을 문자열(YYYY-MM-DD HH:mm:ss)로 반환합니다.
 *
 * @param {string} dateStr - "YYYY.MM" 형식의 날짜 문자열 (예: "2025.05")
 * @returns {{ startDt: string, endDt: string }} - 포맷된 날짜 문자열
 */
export const getMonthStartEndDate = (dateStr) => {
  const [year, month] = dateStr.split('-').map(Number); // month는 1-based

  const startDt = moment({
    year,
    month: month - 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
  }).format('YYYY-MM-DD HH:mm:ss');
  const endDt = moment({ year, month: month - 1, day: 1 })
    .endOf('month')
    .set({ hour: 23, minute: 59, second: 59 })
    .format('YYYY-MM-DD HH:mm:ss');

  return {
    startDt,
    endDt,
  };
};

/**
 * 사용자 기준 스케줄링 월을 계산합니다.
 *
 * - 매월 1일부터 5일 오전 4시 59분까지는 해당 월을 반환합니다.
 * - 매월 5일 오전 5시 이후부터 말일까지는 다음 달을 반환합니다.
 * - 반환 형식은 "YYYY.MM" 입니다 (예: "2025.06").
 *
 * @returns {string} 계산된 사용자 기준 스케줄링 월 ("YYYY.MM" 형식)
 */
export const getUserScheduledMonth = () => {
  const now = moment();
  const day = now.date();
  const hour = now.hour();
  const minute = now.minute();

  const isBeforeCutoff =
    day < 5 || (day === 5 && (hour < 5 || (hour === 5 && minute === 0)));

  const targetDate = isBeforeCutoff ? now.clone() : now.clone().add(1, 'month');
  return targetDate.format('YYYY.MM');
};

/**
 * @desc 날짜 문자열을 "YYYY-MM-DDTHH:mm:ss" 형식으로 포맷하는 함수
 * @param {string} dateString - "Thu Sep 04 2025 13:17:50 GMT+0900 (한국 표준시)" 형식의 날짜 문자열
 * @returns "YYYY-MM-DDTHH:mm:ss" 형식의 날짜 문자열
 */
export const formatDateDateTime = (dateString) => {
  return moment(dateString).format('YYYY-MM-DD HH:mm:ss');
};
/**
 * @desc 두 날짜 사이의 차이를 초 단위로 반환하는 함수
 * @param {string | Date} start - 시작 날짜
 * @param {string | Date} end - 종료 날짜
 * @returns {number} 두 날짜 사이의 차이 (초)
 */
export const getDiffSeconds = (start, end) => {
  return Math.abs(moment(end).diff(moment(start), 'seconds'));
};

/**
 * @desc Unix timestamp를 "YYYY-MM-DD HH:mm:ss" 형식으로 포맷하는 함수
 * @param {number} timestamp - Unix timestamp (초 단위)
 * @returns "YYYY-MM-DD HH:mm:ss" 형식의 날짜 문자열
 */
export const formatUnixToDateTime = (timestamp) => {
  return moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
};

/**
 * @desc 날짜 문자열을 Unix timestamp(초 단위)로 변환하는 함수
 * @param {string | Date} dateString - 날짜 정보
 * @returns {number} Unix timestamp (초 단위)
 */
export const formatDateToUnix = (dateString) => {
  return moment(dateString).unix();
};
